import { CheckCircle, WarningCircle } from '@phosphor-icons/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { authApi } from '../api'
import { AuthLayout } from '../components/auth-layout'
import { Notice } from '../components/notice'
import { PasswordField } from '../components/password-field'
import { isInvalidEmailTokenError } from '../error-keys'
import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import {
  resetPasswordSchema,
  zodResolver,
  type ResetPasswordValues,
} from '../schemas'

type ResetStatus = 'form' | 'success' | 'invalid'

/**
 * Password reset landing (`/reset-password?token=`). Expired/used tokens show
 * the frozen invalid-link copy; transient failures keep the form available.
 */
export function ResetPasswordScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')?.trim() ?? ''

  const [status, setStatus] = useState<ResetStatus>(token ? 'form' : 'invalid')
  const [busy, setBusy] = useState(false)
  const [apiErrorKey, setApiErrorKey] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onSubmit',
  })

  const fieldError = (message?: string) => (message ? t(message) : undefined)

  async function submit(values: ResetPasswordValues) {
    setBusy(true)
    setApiErrorKey(null)
    try {
      await authApi.resetPassword(token, values.password)
      setStatus('success')
    } catch (error) {
      if (isInvalidEmailTokenError(error)) {
        setStatus('invalid')
      } else {
        setApiErrorKey(friendlyErrorKey(error))
      }
    } finally {
      setBusy(false)
    }
  }

  function goToLogin() {
    navigate('/login', { replace: true })
  }

  return (
    <AuthLayout
      title={t('screens.resetPassword.title')}
      maxWidth={520}
      backTo="/login"
    >
      {status === 'success' ? (
        <div className="flex flex-col items-center text-center">
          <CheckCircle
            size={64}
            weight="bold"
            aria-hidden="true"
            className="text-teal"
          />
          <Notice tone="success" className="mt-5 w-full">
            {t('auth.reset.success')}
          </Notice>
          <button
            type="button"
            className="btn-primary mt-6 w-full"
            onClick={goToLogin}
          >
            {t('auth.reset.goToLogin')}
          </button>
        </div>
      ) : null}

      {status === 'invalid' ? (
        <div className="flex flex-col items-center text-center">
          <WarningCircle
            size={64}
            weight="bold"
            aria-hidden="true"
            className="text-danger"
          />
          <Notice tone="error" className="mt-5 w-full">
            {t('auth.reset.invalidToken')}
          </Notice>
          <button
            type="button"
            className="btn-primary mt-6 w-full"
            onClick={goToLogin}
          >
            {t('auth.reset.goToLogin')}
          </button>
        </div>
      ) : null}

      {status === 'form' ? (
        <form
          className="space-y-3"
          noValidate
          onSubmit={handleSubmit((values) => void submit(values))}
        >
          <PasswordField
            label={t('auth.reset.newPassword')}
            autoComplete="new-password"
            hint={t('auth.reset.helper')}
            error={fieldError(errors.password?.message)}
            {...register('password')}
          />
          <PasswordField
            label={t('auth.reset.confirmPassword')}
            autoComplete="new-password"
            error={fieldError(errors.confirmPassword?.message)}
            {...register('confirmPassword')}
          />

          {apiErrorKey ? <Notice tone="error">{t(apiErrorKey)}</Notice> : null}

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={busy}
            aria-busy={busy}
          >
            {busy ? t('auth.reset.submitting') : t('auth.reset.submit')}
          </button>
        </form>
      ) : null}
    </AuthLayout>
  )
}
